import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import Config from "../Config.json";
import { useNavigate } from "react-router-dom";
import Markdown from "markdown-to-jsx";
import {
  postFirstData,
  postSecondData,
  postThirdData,
  getArticleIds,
  updateGlobalData,
  updatePersonalData,
} from "../Fetching";

const TITLE = Config.SITE_TITLE + " | Outils d'IA";
const DESC = "Create a React App from an HTML Website.";
const CANONICAL = Config.SITE_DOMAIN + "/";

const WriteArticle = () => {
  const navigate = useNavigate();
  const topButtonRef = useRef(null);
  const [artIDList, setArtIDList] = useState([]);
  const [markdownContent, setMarkdownContent] = useState("");
  const [previewContent, setPreviewContent] = useState(""); // Új state a preview tartalomnak
  const [formData, setFormData] = useState({
    artID: "",
    image: "",
    alt: "",
    category: "",
    title: "",
    teaser: "",
    language: "",
    markdown: "",
  });

  useEffect(() => {
    const mybutton = topButtonRef.current;
    if (!mybutton) return;

    const scrollFunction = () => {
      mybutton.style.display = window.scrollY > 100 ? "block" : "none";
    };

    const topFunction = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("scroll", scrollFunction);
    mybutton.addEventListener("click", topFunction);

    return () => {
      window.removeEventListener("scroll", scrollFunction);
      mybutton.removeEventListener("click", topFunction);
    };
  }, []);

  useEffect(() => {
    const fetchArtIDs = async () => {
      try {
        const data = await getArticleIds();
        setArtIDList(data);
      } catch (error) {
        console.error("Hiba az artID lekérésekor:", error);
      }
    };

    fetchArtIDs();
  }, []);
  const handlePreview = () => {
    const textareaValue = document.getElementById("markdown").value;
    setPreviewContent(textareaValue);
  };

  const handleSelectChange = (e) => {
    setFormData({ ...formData, artID: e.target.value });
  };

  // Input mezők kezelése
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  // ArtID frissítése
  const refreshArtIDs = async () => {
    try {
      const data = await getArticleIds();
      setArtIDList(data);
    } catch (error) {
      console.error("Hiba az ArtID frissítésekor:", error);
    }
  };

  // Adatok elküldése az adatbázisba
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ellenőrizzük az adatokat beküldés előtt
    console.log("Beküldött adatok:", formData);

    // Ha egy mező üres, állítsuk be NULL-ra vagy egy alapértelmezett értékre
    const cleanedData = {
      artID: formData.artID || null,
      image: formData.image || "",
      category: formData.category || null,
      alt: formData.alt || "",
      title: formData.title || "",
      teaser: formData.teaser || "",
      language: formData.language || null,
      markdown: formData.markdown || "",
    };

    try {
      await postFirstData(
        cleanedData.artID,
        cleanedData.image,
        cleanedData.category
      );
      await postSecondData(
        cleanedData.artID,
        cleanedData.alt,
        cleanedData.title,
        cleanedData.teaser,
        cleanedData.language
      );
      await postThirdData(
        cleanedData.artID,
        cleanedData.title,
        cleanedData.markdown,
        cleanedData.language
      );

      console.log("Sikeres mentés!");
      alert("Sikeresen mentve!");
    } catch (error) {
      console.error("Hiba a mentés során:", error);
      alert("Hiba történt a mentés során.");
    }
  };

  const handleInsertPersonalData = async () => {
    try {
      await postSecondData(
        formData.artID,
        formData.alt,
        formData.title,
        formData.teaser,
        formData.language
      );
      console.log("Személyes adatok mentve (postSecondData)");

      await postThirdData(
        formData.artID,
        formData.title,
        formData.markdown,
        formData.language
      );
      console.log("Személyes adatok mentve (postThirdData)");

      alert("Személyes adatok sikeresen mentve!");
    } catch (error) {
      console.error("Hiba a személyes adatok mentése közben:", error);
      alert("Hiba történt a személyes adatok mentése során.");
    }
  };

  const handleUpdateGlobalData = async () => {
    try {
      await updateGlobalData(formData.artID, formData.image, formData.category);
      console.log("Globális adatok frissítve (updateGlobalData)");
      alert("Globális adatok sikeresen frissítve!");
    } catch (error) {
      console.error("Hiba a globális adatok frissítése közben:", error);
      alert("Hiba történt a globális adatok frissítése során.");
    }
  };

  const categoryOptions = {
    Trend: 1,
    "Machine Learning": 2,
    "AI Tools": 3,
    "AI Games": 4,
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    setFormData({ ...formData, category: categoryOptions[selectedCategory] });
  };

  const handleUpdatePersonalData = async () => {
    try {
      await updatePersonalData(
        formData.artID,
        formData.alt,
        formData.title,
        formData.teaser,
        formData.markdown,
        formData.language
      );
      console.log("Személyes adatok frissítve (updatePersonalData)");
      alert("Személyes adatok sikeresen frissítve!");
    } catch (error) {
      console.error("Hiba a személyes adatok frissítése közben:", error);
      alert("Hiba történt a személyes adatok frissítése során.");
    }
  };

  return (
    <main className="main-content">
      <Helmet>
        <title>{TITLE}</title>
        <link rel="canonical" href={CANONICAL}></link>
        <meta name="description" content={DESC} />
        <meta name="theme-color" content={Config.THEME_COLOR} />
      </Helmet>
      <div className="d-block w-75 content-center px-4 my-4 border border-secondary">
        <form onSubmit={handleSubmit}>
          <div className="row my-3">
            <div className="col-2">
              <div className="mb-3">
                <label htmlFor="artID" className="form-label">
                  Article ID
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="artID"
                  onChange={handleChange}
                  value={formData.artID}
                  required
                />
              </div>
            </div>
            <div className="col-3">
              <div className="mb-3">
                <label htmlFor="SelectArtID" className="form-label">
                  Már létező Article ID
                </label>
                <select
                  className="form-control"
                  id="SelectArtID"
                  onChange={handleSelectChange}
                >
                  <option value="">Válassz egy cikket</option>
                  {artIDList.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-5">
              <div className="mb-3">
                <label htmlFor="image" className="form-label">
                  Image
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="image"
                  onChange={handleChange}
                  value={formData.image}
                />
              </div>
            </div>
            <div className="col-2">
              <div className="col-2">
                <div className="mb-3">
                  <label htmlFor="category" className="form-label">
                    Category
                  </label>
                  <select
                    className="form-control"
                    id="category"
                    onChange={handleCategoryChange}
                  >
                    <option value="">Válassz kategóriát</option>
                    {Object.keys(categoryOptions).map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="row my-3">
            <div className="col-2">
              <div className="mb-3">
                <label htmlFor="language" className="form-label">
                  Language
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="language"
                  onChange={handleChange}
                  value={formData.language}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="title" className="form-label">
                  Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="title"
                  onChange={handleChange}
                  value={formData.title}
                />
              </div>
            </div>
            <div className="col-3">
              <div className="mb-3">
                <label htmlFor="alt" className="form-label">
                  Alt tag
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="alt"
                  onChange={handleChange}
                  value={formData.alt}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="teaser" className="form-label">
                  Teaser
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="teaser"
                  onChange={handleChange}
                  value={formData.teaser}
                />
              </div>
            </div>
            <div className="col-7">
              <div className="mb-3">
                <label htmlFor="markdown" className="form-label">
                  Markdown Content
                </label>
                <textarea
                  className="form-control"
                  id="markdown"
                  onChange={handleChange}
                  value={formData.markdown}
                  rows="10"
                ></textarea>
              </div>
            </div>
          </div>
          <div className="my-3">
            <button type="submit" className="btn btn-success">
              Submit
            </button>
            <button
              type="button"
              className="btn btn-warning mx-2"
              onClick={handlePreview}
            >
              Preview
            </button>
            <button
              type="button"
              className="btn btn-secondary mx-2"
              onClick={handleInsertPersonalData}
            >
              Insert Personal Data
            </button>

            <button
              type="button"
              className="btn btn-warning mx-2"
              onClick={handleUpdateGlobalData}
            >
              Update Card Global Data
            </button>

            <button
              type="button"
              className="btn btn-warning mx-2"
              onClick={handleUpdatePersonalData}
            >
              Update Card Personal Data
            </button>

            <button
              type="button"
              className="btn btn-info mx-2"
              onClick={refreshArtIDs}
            >
              Refresh Article ID
            </button>
          </div>
        </form>
      </div>

      <div className="w-75 content-center markdown-content px-4 md-3">
        {/* "Go to top" gomb */}
        <button
          ref={topButtonRef}
          id="TopBtn"
          title="Go to top"
          style={{ display: "none" }}
        >
          &#10224;
        </button>

        {/* Markdown tartalom megjelenítése */}
        {previewContent ? (
          <Markdown>{previewContent}</Markdown>
        ) : (
          <p>Nincs megjeleníthető tartalom.</p>
        )}

        {/* Vissza gomb */}
        <button className="btn btn-secondary my-3">Vissza</button>
      </div>
    </main>
  );
};

export default WriteArticle;
